// src/components/intake/bodies/TwoPartBody.jsx
import React, { useEffect, useRef } from "react";
import { Button } from "../../ui";
import { OTHER_MAX, OptionCard, OptionGrid, TextAnswer } from "../IntakeParts";
import { PART_KEYS, toggled } from "../intakeRules";
import { PART_OF, Q3_PARTS, SELECT_ALL, SELECT_ALL_NAMED } from "../intakeCopy";

// Q3 (kind `twoPart`): two numbered parts on the page at once (DESIGN.md
// "Numbered option groups"), each with Select all and its own Other box, and
// each option set named by its part's H2. `focusPart` (an old /q3 bookmark's
// tab, or a dashboard link) moves focus to that part's heading on arrival.
// `Subheading` is "h3" where the body sits under an H2 (the Questions page).
export default function TwoPartBody({
  page,
  draft,
  onChange,
  idPrefix,
  focusPart,
  Subheading = "h2",
}) {
  const headings = useRef({});
  useEffect(() => {
    if (focusPart) headings.current[focusPart]?.focus();
  }, [focusPart]);

  return PART_KEYS.map((part, i) => {
    const copy = Q3_PARTS[part];
    const options = page.questions[i].options;
    const value = draft[part];
    const headingId = `${idPrefix}-${part}-h`;
    const set = (next) => onChange({ ...draft, [part]: { ...value, ...next } });
    return (
      <section
        key={part}
        aria-labelledby={headingId}
        className="tw-relative tw-mt-8 tw-pl-12"
      >
        <span
          aria-hidden="true"
          className="tw-absolute tw-bottom-0 tw-left-4 tw-top-12 tw-w-0.5 tw-bg-divider"
        />
        <div className="-tw-ml-12 tw-flex tw-items-start tw-gap-3">
          <span
            aria-hidden="true"
            className="tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-tile tw-bg-accent-tint tw-text-md tw-font-bold tw-text-text-accent"
          >
            {i + 1}
          </span>
          <div className="tw-min-w-0 tw-flex-1">
            <Subheading
              id={headingId}
              tabIndex={-1}
              ref={(el) => {
                headings.current[part] = el;
              }}
              className="tw-m-0 tw-text-lg tw-font-bold tw-text-text focus:tw-outline-none"
            >
              {copy.heading}{" "}
              <span className="tw-font-regular tw-text-text-muted">
                {PART_OF(i + 1, PART_KEYS.length)}
              </span>
            </Subheading>
            <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-text-muted">
              {copy.lede}
            </p>
          </div>
          <Button
            variant="text"
            aria-label={SELECT_ALL_NAMED(copy.heading)}
            className="tw-self-center"
            onClick={() => set({ selected: [...options] })}
          >
            {SELECT_ALL}
          </Button>
        </div>
        <OptionGrid labelledBy={headingId}>
          {options.map((option) => (
            <OptionCard
              key={option}
              label={option}
              checked={value.selected.includes(option)}
              mark={value.selected.includes(option) ? "✓" : null}
              onChange={() =>
                set({ selected: toggled(options, value.selected, option) })
              }
            />
          ))}
        </OptionGrid>
        <TextAnswer
          id={`${idPrefix}-${part}-other`}
          label={copy.other}
          value={value.other}
          max={OTHER_MAX}
          onChange={(other) => set({ other })}
        />
      </section>
    );
  });
}
